<template>
<div class="ablaufgitter" @keydown.esc="auswahl = null">

    <!-- Vorrat oben: er schrumpft, das Gitter darunter fuellt sich. -->
    <h2>Sprüche</h2>
    <p v-if="vorrat.length === 0" class="hinweis">Alle Sprüche liegen im Gitter.</p>
    <div v-else class="vorrat">
        <button v-for="k in vorrat" :key="k.id"
            class="karte" :class="{ gewaehlt: auswahl === k }"
            :aria-pressed="auswahl === k"
            :disabled="geloest"
            @click="waehlen(k)">
            {{ k.text }}
        </button>
    </div>

    <!-- Gitter: eine Spalte pro Station, eine Zeile pro Zug, wie die Tabelle
         im Behelf. Zeile fuer Zeile im DOM, damit der Tabulator zeilenweise
         laeuft. -->
    <div class="gitter" :style="{ gridTemplateColumns: '2.5rem repeat(' + stationen.length + ', minmax(0, 1fr))' }">
        <span class="kopf"></span>
        <span v-for="(s, j) in stationen" :key="s.rolle" class="kopf" :class="'farbe-' + j">{{ s.name }}</span>

        <template v-for="(z, i) in zuege" :key="z.nr">
            <span class="zugnummer">{{ i + 1 }}</span>
            <button v-for="(s, j) in stationen" :key="s.rolle"
                class="zelle" :class="zellenKlasse(i, s.rolle, j)"
                :aria-label="zellenLabel(i, s)"
                :disabled="geloest"
                @click="zelleAntippen(i, s.rolle)">
                <template v-if="belegung[schluessel(i, s.rolle)]">
                    <span class="kartentext">{{ belegung[schluessel(i, s.rolle)].text }}</span>
                    <span v-if="markierung[schluessel(i, s.rolle)]" class="urteil">{{ URTEIL[markierung[schluessel(i, s.rolle)]] }}</span>
                </template>
            </button>
        </template>
    </div>

    <div class="knopfleiste">
        <button @click="pruefen()" :disabled="geloest">Prüfen</button>
        <button @click="zuruecksetzen()">Zurücksetzen</button>
        <slot name="rechts" />
    </div>

    <p class="meldung" role="status" aria-live="polite">{{ meldung }}</p>

</div>
</template>

<script>
import { ref } from 'vue';
import { mische } from '@/logic/Uebung';

// Was auf einer Karte steht, nachdem geprueft wurde. Farbe traegt nie
// allein: Text und Rahmenart unterscheiden sich auch.
const URTEIL = {
    stimmt: 'Stimmt',
    zeitpunkt: 'Richtige Station, falscher Zeitpunkt',
    station: 'Falsche Station'
}

// Uebungsart "Ablaufgitter": die fertigen Sprueche eines Ablaufs in ein
// Raster legen, eine Spalte pro Station, eine Zeile pro Zug. Wer spricht und
// wann, beides zugleich. Prinzip und Oberflaeche nach Import/Ablaufgitter …md,
// das Uebrige wie bei den anderen Uebungsarten.
//
// Bedienung: Karte antippen, dann Zelle antippen. Nochmals dieselbe Karte =
// Auswahl weg. Platzierte Karte antippen = zurueck in den Vorrat (ans Ende).
// Auf eine belegte Zelle legen = tauschen.
//
// Pruefen: mit Karten im Vorrat nur ein Hinweis, wie viele fehlen (zaehlt
// nicht als Versuch). Sonst ein Urteil pro Karte; verschieben geht weiter.
//
// Vertrag mit dem Container wie bei jeder Uebungsart (siehe AllesSortieren.vue).
export default {
    inheritAttrs: false,
    props: {
        uebung: {type: Object, required: true}
    },
    emits: ['geprueft'],
    setup(props, { emit }) {
        const zuege = props.uebung.zuege
        const spruchVon = (z) => z.teile.map(t => t.text).join(' ')

        // Die Stationen in der Reihenfolge ihres ersten Auftritts: wer ruft,
        // steht links.
        const stationen = []
        for (const z of zuege) {
            if (!stationen.some(s => s.rolle === z.spricht)) stationen.push({ rolle: z.spricht, name: z.station.rufname })
        }

        const karten = zuege.map(z => ({ id: 'k' + z.nr, rolle: z.spricht, text: spruchVon(z) }))

        const vorrat = ref(mische(karten))
        const belegung = ref({})      // 'zeile|rolle' -> Karte
        const auswahl = ref(null)
        const markierung = ref({})    // 'zeile|rolle' -> stimmt | zeitpunkt | station
        const meldung = ref('')
        const geloest = ref(false)

        const schluessel = (zeile, rolle) => zeile + '|' + rolle

        // Jede Aenderung laesst das Urteil vergehen; es gilt nur fuer das,
        // was beim Pruefen lag.
        const geaendert = () => {
            markierung.value = {}
            meldung.value = ''
        }

        const waehlen = (k) => {
            auswahl.value = auswahl.value === k ? null : k
        }

        const zelleAntippen = (zeile, rolle) => {
            const s = schluessel(zeile, rolle)
            const da = belegung.value[s]
            if (auswahl.value) {
                vorrat.value = vorrat.value.filter(k => k !== auswahl.value)
                if (da) vorrat.value.push(da)
                belegung.value[s] = auswahl.value
                auswahl.value = null
                geaendert()
            } else if (da) {
                delete belegung.value[s]
                vorrat.value.push(da)
                geaendert()
            }
        }

        const urteil = (zeile, rolle, k) => {
            const soll = zuege[zeile]
            if (k.rolle !== rolle) return 'station'
            // Verglichen wird der Text: zwei gleich lautende Sprueche derselben
            // Station sind austauschbar.
            return soll.spricht === rolle && spruchVon(soll) === k.text ? 'stimmt' : 'zeitpunkt'
        }

        const pruefen = () => {
            const fehlen = vorrat.value.length
            if (fehlen > 0) {
                meldung.value = fehlen === 1 ? 'Es fehlt noch 1 Spruch.' : 'Es fehlen noch ' + fehlen + ' Sprüche.'
                return
            }
            const m = {}
            for (const [s, k] of Object.entries(belegung.value)) {
                const [zeile, rolle] = s.split('|')
                m[s] = urteil(Number(zeile), rolle, k)
            }
            const sitzt = Object.values(m).every(u => u === 'stimmt')
            markierung.value = m
            meldung.value = sitzt ? 'Das sitzt.' : 'Das stimmt noch nicht.'
            if (sitzt) geloest.value = true
            emit('geprueft', { sitzt, geloest: sitzt })
        }

        const zuruecksetzen = () => {
            vorrat.value = mische(karten)
            belegung.value = {}
            auswahl.value = null
            geloest.value = false
            geaendert()
        }

        const zellenKlasse = (zeile, rolle, j) => {
            const s = schluessel(zeile, rolle)
            const belegt = !!belegung.value[s]
            return {
                belegt,
                ['farbe-' + j]: belegt,
                bereit: !belegt && !!auswahl.value,
                [markierung.value[s]]: !!markierung.value[s]
            }
        }

        const zellenLabel = (zeile, s) => {
            const k = belegung.value[schluessel(zeile, s.rolle)]
            return s.name + ', Zug ' + (zeile + 1) + ', ' + (k ? 'belegt mit ' + k.text : 'leer')
        }

        return { URTEIL, zuege, stationen, vorrat, belegung, auswahl, markierung, meldung, geloest,
                 schluessel, waehlen, zelleAntippen, pruefen, zuruecksetzen, zellenKlasse, zellenLabel }
    }
}
</script>

<style scoped>
.ablaufgitter {
    /* Stationsfarben: Kopf und belegte Zelle. Bewusst nicht Oliv und Gelb,
       die gehoeren der Rueckmeldung. */
    --station-0: var(--zs-blau);
    --station-0-hell: var(--zs-blau-hell);
    --station-1: var(--zs-orange-dunkel);
    --station-1-hell: #FBEFE3;
    --station-2: var(--zs-steingrau);
    --station-2-hell: #EEEFF1;
    --gelb: #B08800;
    --gelb-hell: #FFF4CC;
}

h2 {
    margin: 0 0 0.6rem;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--zs-steingrau);
}

.hinweis {
    margin: 0 0 1.5rem;
    color: var(--zs-steingrau);
    font-size: 0.95rem;
}

button {
    font: inherit;
    color: var(--zs-anthrazit);
    cursor: pointer;
    transition: border-color 0.12s, background-color 0.12s;
}

button:disabled {
    cursor: default;
}

button:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

/* ─── Vorrat ─────────────────────────────────────────────── */

.vorrat {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
}

.karte {
    max-width: 100%;
    padding: 0.6rem 0.85rem;
    line-height: 1.35;
    text-align: left;
    background: #ffffff;
    border: 1px solid rgba(31, 35, 40, 0.18);
    border-radius: 8px;
}

.karte:hover:not(:disabled) {
    border-color: var(--zs-orange);
}

.karte.gewaehlt {
    border: 3px solid var(--zs-anthrazit);
    padding: calc(0.6rem - 2px) calc(0.85rem - 2px);
    box-shadow: 0 4px 12px rgba(31, 35, 40, 0.15);
}

/* ─── Gitter ─────────────────────────────────────────────── */

.gitter {
    display: grid;
    gap: 0.4rem;
    margin-bottom: 1.5rem;
}

.kopf {
    padding: 0.45rem 0.5rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: #ffffff;
    border-radius: 6px;
}

.kopf.farbe-0 { background: var(--station-0); }
.kopf.farbe-1 { background: var(--station-1); }
.kopf.farbe-2 { background: var(--station-2); }

.zugnummer {
    align-self: center;
    font-size: 0.9rem;
    font-variant-numeric: tabular-nums;
    color: var(--zs-steingrau);
    text-align: center;
}

.zelle {
    min-height: 3.6rem;
    padding: 0.5rem 0.6rem;
    line-height: 1.35;
    text-align: left;
    background: transparent;
    border: 2px dashed rgba(31, 35, 40, 0.22);
    border-radius: 8px;
}

/* Eine Karte ist gewaehlt: alle leeren Zellen zeigen, dass sie aufnehmen. */
.zelle.bereit {
    border-style: solid;
    border-color: var(--zs-anthrazit);
    background: var(--zs-warmweiss);
}

.zelle.belegt {
    border-style: solid;
}

.zelle.belegt.farbe-0 { border-color: var(--station-0); background: var(--station-0-hell); }
.zelle.belegt.farbe-1 { border-color: var(--station-1); background: var(--station-1-hell); }
.zelle.belegt.farbe-2 { border-color: var(--station-2); background: var(--station-2-hell); }

/* Nach dem Pruefen: das Urteil ueberdeckt die Stationsfarbe. Stimmt =
   durchgezogen, Fehler = doppelt; dazu der Text auf der Karte. */
.zelle.belegt.stimmt {
    border: 2px solid var(--zs-oliv);
    background: var(--zs-oliv-hell);
}

.zelle.belegt.zeitpunkt,
.zelle.belegt.station {
    border: 4px double var(--gelb);
    background: var(--gelb-hell);
}

.kartentext {
    display: block;
}

.urteil {
    display: block;
    margin-top: 0.35rem;
    font-size: 0.85rem;
    font-weight: 600;
}

/* ─── Knopfleiste und Rueckmeldung ───────────────────────── */

.knopfleiste {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    padding-top: 1.25rem;
    border-top: 1px solid rgba(31, 35, 40, 0.1);
}

.knopfleiste > button {
    padding: 0.6rem 1.1rem;
    background: #ffffff;
    border: 1px solid rgba(31, 35, 40, 0.15);
    border-radius: 8px;
}

.knopfleiste > button:hover:not(:disabled) {
    border-color: var(--zs-orange);
}

.knopfleiste > button:disabled {
    opacity: 0.5;
}

.meldung {
    min-height: 1.5em;
    margin: 0.9rem 0 0;
    font-weight: 600;
}

@media (prefers-reduced-motion: reduce) {
    button {
        transition: none;
    }
}

/* Funktext in fester Laufweite: Protokolltext, nicht Bedientext. */
.karte,
.kartentext {
    font-family: var(--zs-funkschrift);
}
</style>
